import React, { createContext, useContext, useRef, useCallback, useMemo } from "react";
import useRestaurantWebSocket from "../../hooks/use-websocket";

const WebSocketContext = createContext(null);

const WebSocketProvider = ({ children, url, bunitId, tillId, enabled, debug }) => {
  const rawListenersRef = useRef(new Set());

  const addRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.add(listener);
    return () => rawListenersRef.current.delete(listener);
  }, []);

  const removeRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.delete(listener);
  }, []);

  const handleMessage = useCallback((message) => {
    rawListenersRef.current.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.error("[WebSocketProvider] Listener error:", err);
      }
    });
  }, []);

  const ws = useRestaurantWebSocket({
    url,
    bunitId,
    tillId,
    enabled,
    debug,
    onMessage: handleMessage,
  });

  const value = useMemo(
    () => ({
      connected: ws.connected,
      reconnecting: ws.reconnecting,
      lastMessage: ws.lastMessage,
      visibleOrders: ws.visibleOrders,
      orderUpdateFlag: ws.orderUpdateFlag,
      subscribe: ws.subscribe,
      removeVisibleOrder: ws.removeVisibleOrder,
      removeVisibleOrderByExternalId: ws.removeVisibleOrderByExternalId,
      sendMessage: ws.sendMessage,
      connect: ws.connect,
      disconnect: ws.disconnect,
      reconnect: ws.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
    }),
    [
      ws.connected,
      ws.reconnecting,
      ws.lastMessage,
      ws.visibleOrders,
      ws.orderUpdateFlag,
      ws.subscribe,
      ws.removeVisibleOrder,
      ws.removeVisibleOrderByExternalId,
      ws.sendMessage,
      ws.connect,
      ws.disconnect,
      ws.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
    ],
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return ctx;
};

export { WebSocketProvider, useWebSocket };
